import { getBlockedRegion } from 'src/constants/blockedRegions';
import helpers from 'src/helpers';

/**
 * CON-1705 — the chat page's phone list, built once.
 *
 * Three screens were building this same pair of index-aligned arrays by hand
 * (the Toolbar filter, the "Send to:" selector, and the sidebar chat row), and
 * the pairing is load-bearing: PhoneSelector and PhonesList look an info object
 * up by `options.indexOf(phone)`, so if the two arrays ever drift out of step a
 * number silently shows another number's line type and carrier.
 *
 * Filtering both in one pass is what keeps them aligned. A phone with no
 * lookup row contributes `null` rather than being skipped, so the indexes stay
 * true even when only the second of three phones has been looked up.
 *
 * Takes the contact shape returned by GET /api/contacts/:id and by the chat
 * list payload — both carry phone1..phone3 alongside phone1Info..phone3Info.
 *
 * @param {object} contact
 * @returns {{ phones: string[], phoneInfoList: (object|null)[] }}
 */
export function buildContactPhoneList(contact) {
  const phones = [];
  const phoneInfoList = [];

  const pairs = [
    [contact?.phone1, contact?.phone1Info],
    [contact?.phone2, contact?.phone2Info],
    [contact?.phone3, contact?.phone3Info],
  ];

  pairs.forEach(([phone, phoneInfo]) => {
    if (helpers.isEmpty(phone)) return;
    phones.push(phone);
    phoneInfoList.push(phoneInfo || null);
  });

  return { phones, phoneInfoList };
}

/**
 * CON-1896 — true when the consultant set a textable override on this number.
 *
 * @param {object|null} phoneInfo
 * @returns {boolean}
 */
export function hasUserTextableOverride(phoneInfo) {
  return phoneInfo?.userTextableOverride !== null
    && phoneInfo?.userTextableOverride !== undefined;
}

/**
 * CON-1705 — "this number cannot receive texts", for the chat page's phone
 * surfaces. One definition, because the dropdown and the single-phone display
 * beside the compose box must never disagree about the same number.
 *
 * A blocked-region number is non-textable whatever the carrier lookup says, and
 * those rows carry no phone info at all — the API drops the object when
 * line_type is null — so testing `isTextable === false` alone left them styled
 * exactly like a working mobile.
 *
 * CON-1896 — the consultant's own textable override decides it when present,
 * the same precedence the contact page's Textable Status uses
 * (PhoneDetailsRow): blocked region first, then the override, then the
 * system result. Both GET /api/contacts/:id and the chat list now send
 * `userTextableOverride` (true | false | null) on the phone info object.
 *
 * Render-only. This drives colour and icon; it never gates whether a number can
 * be picked or sent to. The send-side block lives on the server.
 *
 * @param {string} phone
 * @param {object|null} phoneInfo
 * @returns {boolean}
 */
export function isPhoneNonTextable(phone, phoneInfo) {
  if (getBlockedRegion(phone).blocked) return true;
  if (!phoneInfo) return false;
  if (hasUserTextableOverride(phoneInfo)) return phoneInfo.userTextableOverride === false;
  return phoneInfo.isTextable === false;
}
